import {
  SELF_PAY_INSURANCE_VALUE,
  isSelfPayInsuranceFilter,
} from 'client-portal/utils/clinicianSearch/insurance-facet-options';
import {
  commaSeparatedIntsForQueryParam,
  commaSeparatedStringsForQueryParam,
  parseCommaSeparatedInts,
  parseCommaSeparatedStrings,
} from 'client-portal/utils/clinicianSearch/comma-query-param';
import { serviceText } from 'client-portal/utils/clinicianSearch/cpt-service-option-label';
import AvailabilityFacet from 'client-portal/components/request/clinician-search/facet-filters/availability';
import InsuranceFacet from 'client-portal/components/request/clinician-search/facet-filters/insurance';
import LocationFacet from 'client-portal/components/request/clinician-search/facet-filters/location';
import NameFacet from 'client-portal/components/request/clinician-search/facet-filters/name';
import ServiceFacet from 'client-portal/components/request/clinician-search/facet-filters/services';
import SpecialtyFacet from 'client-portal/components/request/clinician-search/facet-filters/specialty';

const PRESELECTION_BASE = ['preselected', 'clinicianId'];
const PRESELECTION_VALUE_TYPES = {
  ints: { parse: parseCommaSeparatedInts, csv: commaSeparatedIntsForQueryParam },
  strings: { parse: parseCommaSeparatedStrings, csv: commaSeparatedStringsForQueryParam },
};
const normIds = list => (list ?? []).map(id => String(id ?? '').trim()).filter(Boolean);

// Order matters (drives pill order). Add one entry per facet per branch.
// `apply(...componentArgs)` -> route-param object (or null to no-op).
export const FACETS = [
  {
    key: 'name',
    component: NameFacet,
    params: ['name'],
    clearKeys: [],
    pill: { text: q => String(q.name ?? '').trim() },
    apply: raw => ({ name: String(raw ?? '').trim() || null }),
  },
  {
    key: 'availability',
    component: AvailabilityFacet,
    params: ['availableDays', 'availableTimes'],
    clearKeys: ['preselectedDays', 'preselectedTimes'],
    preselectionSeeds: [
      { param: 'availableDays', preselectedKey: 'preselectedDays', valueType: 'ints' },
      { param: 'availableTimes', preselectedKey: 'preselectedTimes', valueType: 'ints' },
    ],
    pill: {
      count: q =>
        parseCommaSeparatedInts(q.availableDays).length +
        parseCommaSeparatedInts(q.availableTimes).length,
    },
    apply: (days, times) => ({
      availableDays: commaSeparatedIntsForQueryParam(days ?? []),
      availableTimes: commaSeparatedIntsForQueryParam(times ?? []),
    }),
  },
  {
    key: 'location',
    component: LocationFacet,
    params: ['officeIds'],
    clearKeys: ['preselectedOfficeIds'],
    preselectionSeeds: [
      { param: 'officeIds', preselectedKey: 'preselectedOfficeIds', valueType: 'strings' },
    ],
    pill: { count: q => parseCommaSeparatedStrings(q.officeIds).length },
    apply: ids => ({ officeIds: commaSeparatedStringsForQueryParam(normIds(ids)) }),
  },
  {
    key: 'service',
    component: ServiceFacet,
    params: ['cptCodes'],
    clearKeys: ['preselectedCptCodeIds', 'cptCodeId'],
    preselectionSeeds: [
      { param: 'cptCodes', preselectedKey: 'preselectedCptCodeIds', valueType: 'strings' },
    ],
    pill: { text: (q, ctx) => serviceText(q, ctx) },
    apply: rate => {
      let csv = commaSeparatedStringsForQueryParam(normIds(Array.isArray(rate) ? rate : [rate]));
      return csv ? { cptCodes: csv } : null;
    },
  },
  {
    key: 'specialty',
    component: SpecialtyFacet,
    params: ['specialty'],
    clearKeys: [],
    requiresDiscovery: true,
    pill: { text: q => String(q.specialty ?? '').trim() },
    apply: raw => ({ specialty: String(raw ?? '').trim() || null }),
  },
  {
    key: 'insurance',
    component: InsuranceFacet,
    params: ['insurance'],
    clearKeys: [],
    requiresDiscovery: true,
    pill: {
      text: (q, ctx) => {
        let value = String(q.insurance ?? '').trim();
        if (!value) {
          return '';
        }
        if (isSelfPayInsuranceFilter(value)) {
          return ctx?.intl?.t?.('booking.clinicianSearch.card.selfPay') ?? SELF_PAY_INSURANCE_VALUE;
        }
        return value;
      },
    },
    apply: raw => ({ insurance: String(raw ?? '').trim() || null }),
  },
];

const BY_KEY = Object.fromEntries(FACETS.map(f => [f.key, f]));

export const FACET_KEYS = FACETS.map(f => f.key);
export const MVP_FACET_KEYS = FACETS.filter(f => !f.requiresDiscovery).map(f => f.key);
export const FILTER_PARAM_NAMES = FACETS.flatMap(f => f.params);
export const facetFor = key => BY_KEY[String(key ?? '')];
export const componentFor = key => facetFor(key)?.component ?? null;

export function buildClearQueryParams(keys = []) {
  if (!keys?.length) {
    return {};
  }
  return Object.fromEntries(keys.map(k => [k, null]));
}

export function clearKeysFor(key) {
  let f = facetFor(key);
  return f ? [...f.params, ...f.clearKeys, ...PRESELECTION_BASE] : [];
}

export const PRESELECTION_SEEDS = FACETS.flatMap(f =>
  (f.preselectionSeeds ?? []).map(seed => ({ facetKey: f.key, ...seed }))
);

export const GROUP_SEARCH_PRESELECTION_QUERY_PARAM_KEYS = [
  'preselected',
  ...new Set(PRESELECTION_SEEDS.map(seed => seed.preselectedKey)),
];

export const PRESELECTION_INPUT_MAPPINGS = PRESELECTION_SEEDS.map(
  ({ param, preselectedKey, valueType }) => ({
    from: param,
    to: preselectedKey,
    ...PRESELECTION_VALUE_TYPES[valueType],
  })
);

export function clinicianSearchAllFilterClearKeys() {
  return [
    ...new Set([
      ...FILTER_PARAM_NAMES,
      'clinicianId',
      'cptCodeId',
      ...GROUP_SEARCH_PRESELECTION_QUERY_PARAM_KEYS,
    ]),
  ];
}

export function clinicianSearchClearQueryParams() {
  return {
    pageNumber: 1,
    ...buildClearQueryParams(clinicianSearchAllFilterClearKeys()),
  };
}
